import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const latest = [...movies].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 5);
const hottest = [...movies].sort((a, b) => b.views - a.views).slice(0, 5);

export default function FocusPanel() {
  const featured = movies.find((m) => m.featured) ?? movies[0];

  return (
    <section className="w-full bg-background-50 px-4 md:px-8 py-12 md:py-16">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* 左：焦点作品大图与简介 */}
        <article className="lg:col-span-2 overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
          <div className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
            <img
              src={featured.backdrop}
              alt={`${featured.title} 焦点剧照`}
              title={`${featured.title} 剧情介绍与角色`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/25 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-accent-500 px-2 py-1 font-label text-[11px] text-background-50">焦点推荐</span>
                {featured.genres.map((g) => (
                  <span key={g} className="rounded bg-background-50/20 px-2 py-1 font-label text-[11px] text-background-100 backdrop-blur-sm">
                    {g}
                  </span>
                ))}
              </div>
              <h2 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-3xl">{featured.title}</h2>
            </div>
          </div>
          <div className="p-5 md:p-6">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
              <span className="flex items-center gap-1"><i className="ri-user-star-line"></i> 导演 · {featured.director}</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {featured.year} · {featured.region}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i> {featured.duration}</span>
              <span className="flex items-center gap-1 text-accent-600"><i className="ri-star-fill"></i> {featured.rating.toFixed(1)} 分</span>
            </div>
            <p className="mt-4 text-sm leading-7 text-foreground-700 line-clamp-4">{featured.synopsis}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${featured.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-article-line"></i> 查看详情与角色
              </Link>
              <span className="text-xs text-foreground-500">更新于 {featured.updatedAt}</span>
            </div>
          </div>
        </article>

        {/* 右：最新 + 热门 */}
        <div className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                <i className="ri-history-line mr-1.5 text-primary-600"></i>最新影视
              </h3>
              <Link to="/category" className="font-label text-xs text-accent-600 hover:text-accent-700 cursor-pointer">
                全部 <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <ul className="mt-4 space-y-3">
              {latest.map((m) => (
                <li key={m.id}>
                  <Link to={`/detail/${m.id}`} className="group flex items-center gap-3 cursor-pointer">
                    <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img src={m.poster} alt={`${m.title} 海报`} title={m.title} className="h-full w-full object-top" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-label text-sm font-medium text-foreground-900 group-hover:text-primary-700">{m.title}</p>
                      <p className="mt-0.5 truncate text-xs text-foreground-500">{m.genres.slice(0, 2).join(' · ')}</p>
                    </div>
                    <span className="shrink-0 text-[11px] text-foreground-500">{m.updatedAt.slice(5)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
            <h3 className="font-heading text-lg font-bold text-foreground-950">
              <i className="ri-bar-chart-box-line mr-1.5 text-accent-600"></i>热门榜单
            </h3>
            <ul className="mt-4 space-y-1">
              {hottest.map((m, i) => (
                <li key={m.id}>
                  <Link to={`/detail/${m.id}`} className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-200/60 cursor-pointer">
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-label text-xs font-bold ${
                      i < 3 ? 'bg-accent-500 text-background-50' : 'bg-secondary-200 text-secondary-800'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-label text-sm text-foreground-800 group-hover:text-primary-700">{m.title}</span>
                    <span className="shrink-0 flex items-center gap-1 text-[11px] text-foreground-500">
                      <i className="ri-eye-line"></i>{(m.views / 10000).toFixed(1)}万
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}